import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { fmtDate, fmtMoney, fmtNumber } from '../lib/format';
import { useCan } from '../lib/auth';
import { Button, Callout, Dialog, Empty, ErrorState, Input, Ltr, Select, useConfirm, useToast } from '../components/ui';
import { qtyText } from './Inventory';

type CountItem = { productId: number; sku: string; name: string; category: string | null; unit: string | null; unitsPerPack: number | null; counted: number | null; system?: number | null };
type Count = {
  id: number;
  name: string;
  status: 'open' | 'applied' | 'cancelled';
  appliedAt: string | null;
  summary: { products: number; changed: number; valueBefore: number | null; valueAfter: number | null } | null;
  items: CountItem[];
};

const num = (s: string) => (s.trim() === '' ? null : Number(s.replace(',', '.')));

/**
 * The stock-count screen: walk the warehouse, type the quantity next to each product. Every
 * number is saved as it is typed (two people can count together); the stock changes only when
 * the count is approved (inventory.edit). Counters do not see the system quantity.
 */
export function StockCountPage() {
  const id = Number(useParams().id);
  const key = ['inventory', 'count', id];
  const { data, error, refetch } = useQuery({ queryKey: key, queryFn: () => api<Count>(`/api/inventory/counts/${id}`), refetchInterval: 20_000 });
  const [edits, setEdits] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState<Record<number, 'saving' | 'error'>>({});
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [onlyLeft, setOnlyLeft] = useState(false);
  const [review, setReview] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();
  const can = useCan();
  const manager = can('inventory.edit');

  const categories = useMemo(() => [...new Set((data?.items ?? []).map((i) => i.category ?? 'ללא קטגוריה'))], [data]);
  if (error) return <div className="pick-page"><ErrorState error={error} onRetry={() => refetch()} /></div>;
  if (!data) return <div className="pick-page"><span className="spinner" /></div>;
  const open = data.status === 'open';
  const counted = data.items.filter((i) => i.counted != null).length;

  const save = async (item: CountItem) => {
    const text = edits[item.productId];
    if (text == null) return;
    const v = num(text);
    if (v != null && (!Number.isFinite(v) || v < 0)) {
      setSaving((s) => ({ ...s, [item.productId]: 'error' }));
      return;
    }
    if (v === item.counted) {
      setEdits(({ [item.productId]: _, ...rest }) => rest);
      return;
    }
    setSaving((s) => ({ ...s, [item.productId]: 'saving' }));
    try {
      await api(`/api/inventory/counts/${id}`, { method: 'PUT', body: { productId: item.productId, qty: v } });
      qc.setQueryData<Count>(key, (c) => (c ? { ...c, items: c.items.map((i) => (i.productId === item.productId ? { ...i, counted: v } : i)) } : c));
      setEdits(({ [item.productId]: _, ...rest }) => rest);
      setSaving(({ [item.productId]: _, ...rest }) => rest);
    } catch (e) {
      setSaving((s) => ({ ...s, [item.productId]: 'error' }));
      toast.error(e);
    }
  };

  const s = q.trim().toLowerCase();
  const visible = data.items.filter(
    (i) =>
      (!category || (i.category ?? 'ללא קטגוריה') === category) &&
      (!onlyLeft || i.counted == null || edits[i.productId] != null) &&
      (!s || `${i.name} ${i.sku}`.toLowerCase().includes(s)),
  );
  // grouped by category, in the catalog's order
  const groups = new Map<string, CountItem[]>();
  for (const i of visible) {
    const c = i.category ?? 'ללא קטגוריה';
    groups.set(c, [...(groups.get(c) ?? []), i]);
  }
  const nextInput = (el: HTMLInputElement) => {
    const all = [...document.querySelectorAll<HTMLInputElement>('input[data-count]')];
    all[all.indexOf(el) + 1]?.focus();
  };

  return (
    <div className="pick-page">
      <div className="pick-top no-print">
        <Link to={manager ? '/inventory' : '/inventory/counts'} className="btn sm ghost">→ {manager ? 'מלאי' : 'ספירות'}</Link>
        <span className="spacer" />
        <Button size="sm" variant="ghost" onClick={() => window.print()}>הדפסה</Button>
      </div>
      <header className="pick-head">
        <div className="pick-customer">{data.name}</div>
        <div className="pick-title">
          {open ? 'כל כמות נשמרת מיד. המלאי מתעדכן רק באישור הספירה.' : data.status === 'applied' ? `אושרה ${fmtDate(data.appliedAt)}` : 'הספירה בוטלה'}
        </div>
      </header>

      <div className="pick-progress">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <strong>נספרו {fmtNumber(counted)} מתוך {fmtNumber(data.items.length)}</strong>
          <span className="muted small">{data.items.length ? Math.round((counted / data.items.length) * 100) : 0}%</span>
        </div>
        <div className="row wrap" style={{ gap: 6, marginTop: 8 }}>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="חיפוש מוצר / מק״ט" style={{ flex: '1 1 160px' }} aria-label="חיפוש" />
          <Select value={category} onChange={(e) => setCategory(e.target.value)} style={{ flex: '1 1 140px' }} aria-label="קטגוריה">
            <option value="">כל הקטגוריות</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </Select>
          <Button size="sm" variant={onlyLeft ? 'primary' : 'default'} onClick={() => setOnlyLeft((v) => !v)}>שנותרו לספור</Button>
        </div>
      </div>

      {data.status === 'applied' && data.summary ? (
        <Callout tone="ok">
          {fmtNumber(data.summary.products)} מוצרים נספרו, {fmtNumber(data.summary.changed)} השתנו.
          {data.summary.valueBefore != null && manager && can('profit.view') ? ` שווי לפני ${fmtMoney(data.summary.valueBefore, { round: true })}, אחרי ${fmtMoney(data.summary.valueAfter, { round: true })}.` : ''}
        </Callout>
      ) : null}

      {visible.length === 0 ? <Empty title={onlyLeft ? 'הכל נספר 🎉' : 'אין מוצרים'} /> : null}
      {[...groups.entries()].map(([cat, items]) => (
        <section key={cat} style={{ marginBottom: 14 }}>
          <h3 style={{ margin: '10px 2px 6px', fontSize: 15 }}>{cat} <span className="muted small">({fmtNumber(items.length)})</span></h3>
          <ul className="pick-lines">
            {items.map((i) => {
              const text = edits[i.productId] ?? (i.counted == null ? '' : String(i.counted));
              const st = saving[i.productId];
              const diff = i.system != null && i.counted != null && edits[i.productId] == null ? Math.round((i.counted - i.system) * 1000) / 1000 : null;
              return (
                <li key={i.productId} className={`pick-line ${i.counted != null && edits[i.productId] == null ? 'picked count-done' : ''}`} style={{ cursor: 'default' }}>
                  <div className="pick-body" style={{ flex: 1 }}>
                    <span className="pick-name" style={{ textDecoration: 'none', opacity: 1 }}>{i.name}</span>
                    <span className="pick-sub">
                      <Ltr>{i.sku}</Ltr>
                      {i.unitsPerPack ? ` · חבילה של ${i.unitsPerPack} (חצי חבילה = 0.5)` : ''}
                      {i.system !== undefined ? ` · במערכת: ${qtyText(i.system ?? null)}` : ''}
                      {diff ? <span style={{ color: diff > 0 ? 'var(--ok)' : 'var(--bad)' }}> · הפרש <bdi dir="ltr">{diff > 0 ? '+' : ''}{qtyText(diff)}</bdi></span> : null}
                    </span>
                  </div>
                  <div className="row" style={{ gap: 6, flex: 'none' }}>
                    {st === 'saving' ? <span className="spinner" /> : st === 'error' ? <span style={{ color: 'var(--bad)' }} title="לא נשמר">!</span> : i.counted != null && edits[i.productId] == null ? <span style={{ color: 'var(--ok)', fontWeight: 800 }}>✓</span> : null}
                    <input
                      data-count
                      className="input ltr-input count-input"
                      inputMode="decimal"
                      value={text}
                      disabled={!open}
                      placeholder="כמות"
                      aria-label={`כמות — ${i.name}`}
                      onChange={(e) => setEdits((ed) => ({ ...ed, [i.productId]: e.target.value }))}
                      onBlur={() => save(i)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          nextInput(e.currentTarget);
                        }
                      }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      {open ? (
        <div className="pick-bottom no-print">
          {manager ? (
            <Button variant="primary" disabled={!counted || Object.keys(edits).length > 0} onClick={() => setReview(true)}>
              סיום ואישור הספירה
            </Button>
          ) : (
            <span className="muted small">הספירה נשמרת. מנהל המלאי יאשר אותה בסיום.</span>
          )}
        </div>
      ) : null}
      {review ? <ApplyCount count={data} onClose={() => setReview(false)} /> : null}
    </div>
  );
}

/** Before the stock changes: what differs from the system, and approve (or cancel the count). */
function ApplyCount({ count, onClose }: { count: Count; onClose: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const done = count.items.filter((i) => i.counted != null);
  const diffs = done
    .map((i) => ({ ...i, diff: Math.round((i.counted! - (i.system ?? 0)) * 1000) / 1000 }))
    .filter((i) => i.system == null || i.diff !== 0)
    .sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));

  const apply = async () => {
    setBusy('apply');
    try {
      const r = await api<{ products: number; changed: number }>(`/api/inventory/counts/${count.id}/apply`, { body: {} });
      toast.success(`הספירה אושרה — ${fmtNumber(r.products)} מוצרים עודכנו במלאי`);
      qc.invalidateQueries({ queryKey: ['inventory'] });
      navigate('/inventory');
    } catch (e) {
      toast.error(e);
      setBusy(null);
    }
  };
  const cancel = async () => {
    if (!(await confirm({ title: 'לבטל את הספירה?', body: 'הכמויות שהוקלדו לא ייכנסו למלאי.', danger: true, confirmLabel: 'ביטול הספירה' }))) return;
    setBusy('cancel');
    try {
      await api(`/api/inventory/counts/${count.id}/cancel`, { body: {} });
      qc.invalidateQueries({ queryKey: ['inventory'] });
      navigate('/inventory');
    } catch (e) {
      toast.error(e);
      setBusy(null);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title="אישור הספירה"
      size="lg"
      footer={
        <>
          <Button variant="danger" loading={busy === 'cancel'} onClick={cancel}>ביטול הספירה</Button>
          <span className="spacer" />
          <Button onClick={onClose}>חזרה לספירה</Button>
          <Button variant="primary" loading={busy === 'apply'} onClick={apply}>אישור — עדכון המלאי</Button>
        </>
      }
    >
      <div className="stack">
        <div>
          נספרו <strong>{fmtNumber(done.length)}</strong> מוצרים מתוך {fmtNumber(count.items.length)}. באישור הכמות שנספרה תהיה הכמות במלאי.
          מוצרים שלא נספרו נשארים כמו שהם.
        </div>
        {diffs.length ? (
          <div className="table-wrap" style={{ maxHeight: 360, overflowY: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>מוצר</th>
                  <th className="num">במערכת</th>
                  <th className="num">נספר</th>
                  <th className="num">הפרש</th>
                </tr>
              </thead>
              <tbody>
                {diffs.map((i) => (
                  <tr key={i.productId}>
                    <td className="primary">{i.name}<span className="sub"><Ltr>{i.sku}</Ltr></span></td>
                    <td className="num tabular">{i.system == null ? <span className="faint">לא נספר</span> : qtyText(i.system)}</td>
                    <td className="num tabular strong">{qtyText(i.counted)}</td>
                    <td className="num tabular" style={{ color: i.diff > 0 ? 'var(--ok)' : i.diff < 0 ? 'var(--bad)' : undefined }}>
                      {i.system == null ? '—' : <bdi dir="ltr">{i.diff > 0 ? '+' : ''}{qtyText(i.diff)}</bdi>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Callout tone="ok">כל מה שנספר תואם למערכת.</Callout>
        )}
      </div>
    </Dialog>
  );
}
